import pLimit from "p-limit";
import { useCallback, useRef, useState } from "react";

import { CaptureItemState, CaptureJob, CaptureResult } from "@components/ChartCapture/types";
import { captureJobId } from "@components/ChartCapture/utils/captureJobId";
import { captureJobLabel } from "@components/ChartCapture/utils/captureJobLabel";

export type ProcessOutcome = {
  ok: boolean;
  /** Shown on the item's segment when `ok` is false. */
  error?: string;
};

export type CapturePipelineOptions = {
  /** Max concurrent `processResult` (save) calls. Capture concurrency is set on the runner. */
  saveConcurrency?: number;
};

/**
 * Drives a per-item pipeline: each item renders its chart off-screen (capturing),
 * then runs `processResult` (saving) — the mutation, guarded by the caller. Each
 * item's status is tracked so the UI can show live progress and mark failures.
 * `run(jobs)` resolves with the final item states once every item is terminal.
 *
 * The two stages have independent concurrency caps: capture is bounded by the
 * runner's window; save is bounded here by `saveConcurrency` (p-limit), so a big
 * report can't fire dozens of mutations at once.
 *
 * The caller renders <OffscreenChartCaptureRunner> with `jobs`, `onItemStart`,
 * and `onItemCaptured` whenever `jobs` is non-null.
 */
export const useCapturePipeline = (
  processResult: (job: CaptureJob, result: CaptureResult) => Promise<ProcessOutcome>,
  { saveConcurrency = 10 }: CapturePipelineOptions = {},
) => {
  const [jobs, setJobs] = useState<CaptureJob[] | null>(null);
  const [items, setItems] = useState<CaptureItemState[]>([]);
  const itemsRef = useRef<CaptureItemState[]>([]);
  const remainingRef = useRef(0);
  const resolverRef = useRef<((items: CaptureItemState[]) => void) | null>(null);
  const processRef = useRef(processResult);
  processRef.current = processResult;
  // Bound concurrent saves so a large report doesn't fan out dozens of mutations.
  const [saveLimit] = useState(() => pLimit(saveConcurrency));

  const patch = useCallback((id: string, next: Partial<CaptureItemState>) => {
    itemsRef.current = itemsRef.current.map((item) => (item.id === id ? { ...item, ...next } : item));
    setItems(itemsRef.current);
  }, []);

  const run = useCallback((next: CaptureJob[]): Promise<CaptureItemState[]> => {
    const initial: CaptureItemState[] = next.map((job) => ({
      id: captureJobId(job),
      label: captureJobLabel(job),
      status: "pending",
    }));
    itemsRef.current = initial;
    setItems(initial);
    if (next.length === 0) return Promise.resolve(initial);
    remainingRef.current = next.length;
    setJobs(next);
    return new Promise<CaptureItemState[]>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleItemStart = useCallback(
    (job: CaptureJob) => {
      const id = captureJobId(job);
      // Only advance from pending — never clobber a terminal state.
      if (itemsRef.current.find((item) => item.id === id)?.status === "pending") {
        patch(id, { status: "capturing" });
      }
    },
    [patch],
  );

  const handleItemCaptured = useCallback(
    async (job: CaptureJob, result: CaptureResult) => {
      const id = captureJobId(job);
      patch(id, { status: "saving" });
      let outcome: ProcessOutcome;
      try {
        outcome = await saveLimit(() => processRef.current(job, result));
      } catch (err) {
        console.error("Capture pipeline item failed", err);
        outcome = { ok: false, error: "Unexpected error" };
      }
      patch(id, outcome.ok ? { status: "done", error: undefined } : { status: "error", error: outcome.error });

      remainingRef.current -= 1;
      if (remainingRef.current <= 0) {
        setJobs(null);
        const resolve = resolverRef.current;
        resolverRef.current = null;
        resolve?.(itemsRef.current);
      }
    },
    [patch, saveLimit],
  );

  return { jobs, items, run, handleItemStart, handleItemCaptured };
};
